/**
 * AutoExposure — average-log-luminance eye adaptation (UE "Auto Exposure
 * Basic" / Narkowicz "Automatic Exposure" family).
 *
 * Measurement: small center-weighted regions of the post pipeline's HDR scene
 * pass render target are read back asynchronously (HalfFloat → decoded on
 * CPU), and the geometric mean of luminance (average of log2) is computed —
 * log averaging keeps a bright sky from crushing a dark interior the way an
 * arithmetic mean does. The scene's log-average is treated as middle grey
 * (the photographic 18% convention): target exposure = key / avgLum.
 *
 * Adaptation: exposure eases toward the target with separate up (dark→bright
 * reacts faster) and down speeds via 1 − exp(−dt·speed) — frame-rate
 * independent. The result multiplies a base exposure and is written to
 * `renderer.toneMappingExposure`, which three's PostProcessing output
 * transform consumes.
 *
 * The GPU histogram variant (64-bin compute + percentile clipping) is a
 * documented v2; this readback path works identically on WebGL2 and WebGPU.
 */

import type * as THREE from 'three/webgpu';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';

// ─── CPU math (exported for tests) ──────────────────────────────────────────

/** Decode an IEEE 754 half-precision value from its raw uint16 bits. */
export function halfToFloat(bits: number): number {
  const sign = (bits & 0x8000) ? -1 : 1;
  const exp = (bits >> 10) & 0x1f;
  const frac = bits & 0x3ff;
  if (exp === 0) return sign * frac * 2 ** -24;               // subnormal
  if (exp === 31) return frac ? NaN : sign * Infinity;        // inf / nan
  return sign * (1 + frac / 1024) * 2 ** (exp - 15);
}

/** Rec. 709 relative luminance. */
export function luminance709(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Weighted average of log2 luminance over RGBA pixel blocks. Non-finite and
 * near-zero samples are floored (a single NaN or true-black texel must not
 * poison the mean — the same lesson as the env-map NaN bug).
 */
export function averageLogLuminance(
  blocks: Array<{ pixels: Float32Array; weight: number }>,
): number {
  let sum = 0;
  let wsum = 0;
  for (const { pixels, weight } of blocks) {
    let blockSum = 0;
    let n = 0;
    for (let i = 0; i + 3 < pixels.length; i += 4) {
      let lum = luminance709(pixels[i], pixels[i + 1], pixels[i + 2]);
      if (!Number.isFinite(lum) || lum < 1e-6) lum = 1e-6;
      blockSum += Math.log2(lum);
      n++;
    }
    if (n > 0) {
      sum += (blockSum / n) * weight;
      wsum += weight;
    }
  }
  if (wsum === 0) return Math.log2(0.18);
  return sum / wsum;
}

/** Target exposure multiplier for a measured average luminance. */
export function targetExposureForLuminance(
  avgLuminance: number, key: number, minExposure: number, maxExposure: number,
): number {
  const e = key / Math.max(avgLuminance, 1e-6);
  return Math.min(maxExposure, Math.max(minExposure, e));
}

/** Frame-rate-independent exponential ease toward the target. */
export function adaptExposure(
  current: number, target: number, dt: number, speedUp: number, speedDown: number,
): number {
  const speed = target > current ? speedUp : speedDown;
  return current + (target - current) * (1 - Math.exp(-dt * speed));
}

// ─── Histogram metering (2026-09-15, shader additions wave) ─────────────────
//
// A log-average is dragged by outliers: one sun disc or one black doorway
// swings the whole frame. UE-style histogram metering accumulates log2
// luminance into bins, trims a low and a high PERCENTILE of the pixel
// population, and averages what is left — so a few very bright or very dark
// pixels cannot own the exposure decision. Bins are over the metered SAMPLES
// (the same centre-weighted regions the average reads), not a full-frame GPU
// histogram; the maths is identical, the population smaller.

export const HISTOGRAM_MIN_LOG = -10;
export const HISTOGRAM_MAX_LOG = 10;

/** Weighted log2-luminance histogram over the metered blocks. */
export function histogramLogLuminance(
  blocks: Array<{ pixels: Float32Array; weight: number }>, bins = 64,
  minLog = HISTOGRAM_MIN_LOG, maxLog = HISTOGRAM_MAX_LOG,
): Float32Array {
  const hist = new Float32Array(Math.max(2, bins));
  const span = Math.max(1e-6, maxLog - minLog);
  for (const { pixels, weight } of blocks) {
    for (let i = 0; i + 2 < pixels.length; i += 4) {
      const lum = luminance709(pixels[i], pixels[i + 1], pixels[i + 2]);
      const l = Math.log2(Math.max(lum, 1e-6));
      let b = Math.floor(((l - minLog) / span) * hist.length);
      if (b < 0) b = 0; else if (b >= hist.length) b = hist.length - 1;
      hist[b] += weight;
    }
  }
  return hist;
}

/**
 * Mean log2 luminance of the bins between the low and high percentiles of the
 * population (UE defaults: 50 %..98 % — the shadows and the very brightest are
 * trimmed). Returns 0 (= luminance 1) for an empty histogram.
 */
export function percentileLogLuminance(
  hist: Float32Array, lowPct = 0.5, highPct = 0.98,
  minLog = HISTOGRAM_MIN_LOG, maxLog = HISTOGRAM_MAX_LOG,
): number {
  let total = 0;
  for (let i = 0; i < hist.length; i++) total += hist[i];
  if (total <= 0) return 0;
  const lo = Math.min(lowPct, highPct) * total, hi = Math.max(lowPct, highPct) * total;
  const binW = (maxLog - minLog) / hist.length;
  let acc = 0, sum = 0, w = 0;
  for (let i = 0; i < hist.length; i++) {
    const c = hist[i];
    const from = acc, to = acc + c;
    acc = to;
    if (to <= lo || from >= hi) continue;
    const part = Math.min(to, hi) - Math.max(from, lo);   // the slice of this bin inside the band
    if (part <= 0) continue;
    const centre = minLog + (i + 0.5) * binW;
    sum += centre * part; w += part;
  }
  return w > 0 ? sum / w : 0;
}

/**
 * Exponential adaptation in LOG exposure (stops), with separate time
 * constants for brightening and darkening — the frame-rate-independent form
 * the guide states: a = 1 − e^(−dt/τ). `stops` is log2 of the exposure
 * multiplier, NOT photographic EV100 (whose sign runs the other way).
 */
export function adaptLogExposure(
  previousStops: number, targetStops: number, dtSeconds: number, brightenTauSeconds: number, darkenTauSeconds: number,
): number {
  const tau = targetStops > previousStops ? brightenTauSeconds : darkenTauSeconds;
  if (tau <= 0) return targetStops;
  const a = -Math.expm1(-Math.max(0, dtSeconds) / tau);
  return previousStops + a * (targetStops - previousStops);
}

// ─── Controller ─────────────────────────────────────────────────────────────

export interface AutoExposureConfig {
  /** Middle-grey key value (default 0.18). */
  key?: number;
  /** Exposure multiplier clamp (defaults 0.25 / 4). */
  minExposure?: number;
  maxExposure?: number;
  /** Adaptation speeds, 1/s (defaults: up 3, down 1 — eyes brighten faster). */
  speedUp?: number;
  speedDown?: number;
  /** Frames between measurements (default 10 ≈ 6 Hz at 60 fps). */
  intervalFrames?: number;
  /** Base exposure the adaptation multiplies (default: renderer's at init). */
  baseExposure?: number;
  /** Metering: the log average, or the percentile-trimmed histogram (default 'average'). */
  metering?: 'average' | 'histogram';
  /** Histogram trims: keep the population between these percentiles (defaults 0.5 / 0.98). */
  lowPercentile?: number;
  highPercentile?: number;
  histogramBins?: number;
  /**
   * Adaptation strength 0..1 (default 1): blends the smoothed adaptation
   * toward neutral — applied = base·(1 + (adaptation − 1)·strength). 0 pins
   * exposure at base (metering still runs); 1 is full eye adaptation.
   */
  strength?: number;
}

interface MeterRegion { cx: number; cy: number; size: number; weight: number }

/** Center-weighted 5-region metering pattern (fractions of the RT). */
const METER_REGIONS: MeterRegion[] = [
  { cx: 0.5, cy: 0.5, size: 32, weight: 4 },
  { cx: 0.25, cy: 0.3, size: 16, weight: 1 },
  { cx: 0.75, cy: 0.3, size: 16, weight: 1 },
  { cx: 0.25, cy: 0.7, size: 16, weight: 1 },
  { cx: 0.75, cy: 0.7, size: 16, weight: 1 },
];

export class AutoExposureController {
  readonly config: Required<AutoExposureConfig>;
  private _adaptation = 1;
  private _target = 1;
  private _inFlight = false;

  constructor(config?: AutoExposureConfig, rendererExposure = 1) {
    this.config = {
      key: config?.key ?? 0.18,
      minExposure: config?.minExposure ?? 0.25,
      maxExposure: config?.maxExposure ?? 4,
      speedUp: config?.speedUp ?? 3,
      speedDown: config?.speedDown ?? 1,
      intervalFrames: config?.intervalFrames ?? 10,
      baseExposure: config?.baseExposure ?? rendererExposure,
      strength: config?.strength ?? 1,
      metering: config?.metering ?? 'average',
      lowPercentile: config?.lowPercentile ?? 0.5,
      highPercentile: config?.highPercentile ?? 0.98,
      histogramBins: config?.histogramBins ?? 64,
    };
  }

  /** Switch the metering live (no re-measure needed; the next reading uses it). */
  setMetering(m: 'average' | 'histogram'): void { this.config.metering = m; }

  /** Live adaptation-strength knob (0 = pinned at base, 1 = full). */
  setStrength(v: number): void {
    this.config.strength = Math.max(0, Math.min(1, v));
  }

  /** Current smoothed adaptation multiplier. */
  get adaptation(): number { return this._adaptation; }
  /** Latest measured (unsmoothed) target. */
  get target(): number { return this._target; }
  get measuring(): boolean { return this._inFlight; }

  /**
   * Kick an async measurement from the HDR scene render target. Resolves when
   * the readbacks land; concurrent calls while one is in flight are no-ops
   * (the FeedbackReadback in-flight-guard pattern).
   */
  async measure(renderer: THREE.WebGPURenderer, rt: { width: number; height: number }): Promise<void> {
    if (this._inFlight) return;
    const read = (renderer as unknown as {
      readRenderTargetPixelsAsync?: (
        rt: unknown, x: number, y: number, w: number, h: number,
      ) => Promise<ArrayLike<number> & { constructor: { name: string } }>;
    }).readRenderTargetPixelsAsync;
    if (!read) return;
    this._inFlight = true;
    try {
      const blocks: Array<{ pixels: Float32Array; weight: number }> = [];
      for (const region of METER_REGIONS) {
        const s = Math.min(region.size, rt.width, rt.height);
        const x = Math.max(0, Math.min(rt.width - s, Math.round(region.cx * rt.width - s / 2)));
        const y = Math.max(0, Math.min(rt.height - s, Math.round(region.cy * rt.height - s / 2)));
        const raw = await read.call(renderer, rt, x, y, s, s);
        const pixels = new Float32Array(raw.length);
        if (raw.constructor.name === 'Uint16Array') {
          for (let i = 0; i < raw.length; i++) pixels[i] = halfToFloat(raw[i] as number);
        } else {
          for (let i = 0; i < raw.length; i++) pixels[i] = raw[i] as number;
        }
        blocks.push({ pixels, weight: region.weight });
      }
      const avgLog = this.config.metering === 'histogram'
        ? percentileLogLuminance(histogramLogLuminance(blocks, this.config.histogramBins), this.config.lowPercentile, this.config.highPercentile)
        : averageLogLuminance(blocks);
      this._target = targetExposureForLuminance(
        2 ** avgLog, this.config.key, this.config.minExposure, this.config.maxExposure,
      );
    } catch {
      // A failed read (context loss, resize race) keeps the previous target.
    } finally {
      this._inFlight = false;
    }
  }

  /** Advance adaptation and return the exposure to apply. */
  update(dt: number): number {
    this._adaptation = adaptExposure(
      this._adaptation, this._target, dt, this.config.speedUp, this.config.speedDown,
    );
    // strength blends toward neutral so a UI slider can soften the effect
    // without stopping the metering (re-raising it responds instantly).
    return this.config.baseExposure * (1 + (this._adaptation - 1) * this.config.strength);
  }
}

// ─── System ─────────────────────────────────────────────────────────────────

/**
 * Post-phase system: measures every `intervalFrames`, eases every frame, and
 * writes `renderer.toneMappingExposure`. The render-target provider is a
 * closure so the system stays decoupled from PostProcessingPipeline's
 * internals (pass `() => pipeline.scenePassRenderTarget`).
 */
export class AutoExposureSystem implements ISystem {
  readonly name = 'AutoExposure';
  readonly phase = LoopPhase.Post;
  readonly subPhase = SubPhase.Update;
  readonly priority = 5;

  readonly controller: AutoExposureController;
  private readonly _renderer: THREE.WebGPURenderer;
  private readonly _getTarget: () => { width: number; height: number } | null;
  private _frame = 0;

  constructor(args: {
    renderer: THREE.WebGPURenderer;
    getRenderTarget: () => { width: number; height: number } | null;
    config?: AutoExposureConfig;
  }) {
    this._renderer = args.renderer;
    this._getTarget = args.getRenderTarget;
    this.controller = new AutoExposureController(
      args.config, args.renderer.toneMappingExposure,
    );
  }

  execute(dt: number, _world: World): void {
    this._frame++;
    if (this._frame % this.controller.config.intervalFrames === 0) {
      const rt = this._getTarget();
      // XR guard: mid-session readbacks stall the frame budget — adaptation
      // freezes at its last value while presenting.
      const xr = (this._renderer as unknown as { xr?: { isPresenting?: boolean } }).xr;
      if (rt && !xr?.isPresenting) {
        void this.controller.measure(this._renderer, rt);
      }
    }
    this._renderer.toneMappingExposure = this.controller.update(dt);
  }

  dispose(): void { /* no GPU resources owned */ }
}
